import { ref } from 'vue'
import { defineStore } from 'pinia'
import { alphabetically } from '@/utils/sort'
import { apiClientV1 } from '@/ContextTab/services/apiClient'
import type { NewsSetFilters } from '@/lib/types'
import type {
  DataOfNewsPublishedPresetListGetOut,
  NewsPublishedPresetPatchIn,
} from '@/api/generated/v1'


export type Preset = DataOfNewsPublishedPresetListGetOut

export const usePublishedPresetsStore = defineStore('publishedPresets', () => {

  const presets = ref<Preset[]>([])

  const currentPresetId = ref<number | string | null>(null)

  const getPresetById = (id: number | string) => {
    return presets.value.find((p) => p.id === id)
  }

  async function fetchPresets() {
    const response = await apiClientV1.NewsService.getNewsPublishedPresets()

    if (response.error || !response.data.data) {
      throw new Error('Произошла ошибка при загрузке пресетов')
    }

    presets.value = response.data.data.toSorted(alphabetically('name'))
  }

  async function createPreset(name: string, filters: NewsSetFilters) {
    const response = await apiClientV1.NewsService.postNewsPublishedPresets({
      body: {
        ...filters,
        name,
      },
    })

    if (response.error || !response.data.data) {
      throw new Error(`Ошибка создания пресета: ${response.error?.errors.join(', ')}`)
    }

    presets.value.push(response.data.data)
    presets.value.sort(alphabetically('name'))

    return response.data.data
  }

  async function updatePreset(id: number, filters: NewsPublishedPresetPatchIn) {
    const preset = presets.value.find((p) => p.id === id)

    if (!preset) {
      return `Пресет с id ${id} не найден`
    }

    const response = await apiClientV1.NewsService.patchNewsPublishedPresetsByPresetId({
      path: {
        preset_id: id,
      },
      body: {
        name: preset.name,
        ...filters,
      },
    })

    if (response.error || !response.data?.data) {
      return response.data
    }

    const index = presets.value.findIndex((p) => p.id === id)
    presets[index] = response.data.data

    return response.data.data
  }

  async function renamePreset(id: number, name: string): Promise<void> {
    const preset = presets.value.find((preset) => preset.id === id)

    if (!preset) {
      throw new Error(`Пресет с id ${id} не найден`)
    }

    const response = await apiClientV1.NewsService.patchNewsPublishedPresetsByPresetId({
      path: {
        preset_id: id,
      },
      body: {
        name,
      },
    })

    if (response.error || !response.data?.data) {
      throw new Error('Произошла ошибка при переименовании пресета')
    }
  }

  async function deletePreset(id: number): Promise<void> {
    const response = await apiClientV1.NewsService.deleteNewsPublishedPresetsByPresetId({ path: { preset_id: id } })

    if (response.error) {
      throw new Error('Произошла ошибка при удалении пресета')
    } else {
      if (id === currentPresetId.value) {
        clearPreset()
      }
    }
  }

  function applyPreset(id: number): Preset | { errors: string } {
    const preset = presets.value.find((preset) => preset.id?.valueOf() === id.valueOf())

    if (preset) {
      currentPresetId.value = id
      return preset
    }

    return { errors: `Пресет с id ${id} не найден` }
  }

  function clearPreset() {
    currentPresetId.value = null
  }

  return {
    presets,
    currentPresetId,

    getPresetById,

    applyPreset,
    fetchPresets,
    createPreset,
    updatePreset,
    deletePreset,
    renamePreset,
    clearPreset,
  }
})
